import { ref, readonly } from 'vue';
import {
  getEffectiveTrackMatchStatusId,
  trackMatchStatusRelationFromId,
  trackMatchingStatusHidesCandidates,
} from '@/constants/trackMatchStatus';
import { computeNextMarkRevealState } from '@/trackMatching/trackMatchMarkAction';
import { shouldHideCandidateRowsForLiveTrack } from '@/trackMatching/trackMatchCandidateVisibility';
import { buildTrackMatchingActionForLiveTrack, findRawDdexRowStateByLinkedId } from '@/trackMatching/trackMatchingPayload';
import {
  clearPendingArtistRename,
  clearPendingPrimarySave,
  isArtistRenameQueued as isArtistRenameQueuedOnRow,
  isArtistRenameQueuedForSource as isArtistRenameQueuedForSourceOnRow,
  isIsrcQueued,
  isLinkQueuedForSource,
  liveTrackRowKey,
} from '@/trackMatching/trackMatchingQueueState';
import type {
  SourceType,
  TrackMatchingAction,
  TrackMatchingRowState,
  TrackQueueAction,
} from '@/trackMatching/trackMatchingTypes';
import { SOURCE_TYPES } from '@/trackMatching/trackMatchingTypes';

export { RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE, SOURCE_TYPES } from '@/trackMatching/trackMatchingTypes';

/** Row visibility inputs for {@see processedData} (not ISRC highlight state). */
export type LiveTrackQueueVisibility = {
  unlinkQueued: boolean;
  revealDoneMatches: boolean;
  hasQueuedLink: boolean;
  hideCandidateRows: boolean;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function mergeDeepIncomingWins(
  base: Record<string, unknown>,
  incoming: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };
  for (const [key, incomingVal] of Object.entries(incoming)) {
    if (incomingVal === undefined) continue;
    const baseVal = result[key];
    if (incomingVal === null || !isPlainRecord(incomingVal)) {
      result[key] = incomingVal;
      continue;
    }
    if (isPlainRecord(baseVal)) {
      result[key] = mergeDeepIncomingWins(baseVal, incomingVal);
    } else {
      result[key] = mergeDeepIncomingWins({}, incomingVal);
    }
  }
  return result;
}

function clearQueuedActionsForRow(rowState: TrackMatchingRowState): void {
  if (rowState.source === SOURCE_TYPES.LIVE_TRACK) {
    clearPendingPrimarySave(rowState);
    clearPendingArtistRename(rowState);
    rowState.unlinkQueued = false;
    rowState.markQueued = false;
    rowState.reveal_done_matches = false;
  }
}

function collectQueuedLiveTrackIds(
  rowStates: Record<string, TrackMatchingRowState>
): Set<string | number> {
  const liveTrackIds = new Set<string | number>();
  Object.values(rowStates).forEach((rowState) => {
    if (rowState.source !== SOURCE_TYPES.LIVE_TRACK) {
      return;
    }
    if (
      rowState.pendingPrimarySave ||
      rowState.pendingArtistRename ||
      rowState.unlinkQueued ||
      rowState.markQueued
    ) {
      liveTrackIds.add(rowState.live_track_id);
    }
  });
  return liveTrackIds;
}

const states = ref<Record<string, TrackMatchingRowState>>({});
const optimisticPatches = ref<Record<string | number, Record<string, unknown>>>({});
const rowStatesByLiveTrackId: Record<string | number, TrackMatchingRowState[]> = {};
const queueVisibilityByLiveTrackId = ref<Record<string | number, LiveTrackQueueVisibility>>({});

function rowsForLiveTrack(liveTrackId: string | number): TrackMatchingRowState[] {
  if (!rowStatesByLiveTrackId[liveTrackId]) {
    rowStatesByLiveTrackId[liveTrackId] = [];
  }
  return rowStatesByLiveTrackId[liveTrackId];
}

function attachRowToLiveTrackIndex(rowState: TrackMatchingRowState): void {
  const rows = rowsForLiveTrack(rowState.live_track_id);
  if (!rows.includes(rowState)) {
    rows.push(rowState);
  }
}

function clearRowIndex(): void {
  for (const key of Object.keys(rowStatesByLiveTrackId)) {
    delete rowStatesByLiveTrackId[key];
  }
}

export const useTrackMatchingStore = () => {
  const getLiveTrackRow = (liveTrackId: string | number): TrackMatchingRowState => {
    return getState(liveTrackId, SOURCE_TYPES.LIVE_TRACK, liveTrackId);
  };

  const syncQueueVisibilityForLiveTrack = (liveTrackId: string | number) => {
    const liveTrackRow = states.value[liveTrackRowKey(liveTrackId)];
    const matchData = liveTrackRow?.matchData as Record<string, unknown> | undefined;
    const matchStatusId = getEffectiveTrackMatchStatusId(matchData);
    const pending = liveTrackRow?.pendingPrimarySave;
    const hasQueuedLink = pending?.kind === 'link';
    const next: LiveTrackQueueVisibility = {
      unlinkQueued: !!liveTrackRow?.unlinkQueued,
      revealDoneMatches: liveTrackRow?.reveal_done_matches === true,
      hasQueuedLink,
      hideCandidateRows: shouldHideCandidateRowsForLiveTrack({
        hasUnlinkAction: !!liveTrackRow?.unlinkQueued,
        effectiveRawDdexTrackId: matchData?.raw_ddex_track_id,
        matchStatusId,
        revealDoneMatches: liveTrackRow?.reveal_done_matches === true,
        hasPendingLinkSelection: hasQueuedLink,
      }),
    };
    const previous = queueVisibilityByLiveTrackId.value[liveTrackId];
    if (
      previous &&
      previous.unlinkQueued === next.unlinkQueued &&
      previous.revealDoneMatches === next.revealDoneMatches &&
      previous.hasQueuedLink === next.hasQueuedLink &&
      previous.hideCandidateRows === next.hideCandidateRows
    ) {
      return;
    }
    queueVisibilityByLiveTrackId.value = {
      ...queueVisibilityByLiveTrackId.value,
      [liveTrackId]: next,
    };
  };

  const getQueueVisibilityForLiveTrack = (liveTrackId: string | number): LiveTrackQueueVisibility => {
    return (
      queueVisibilityByLiveTrackId.value[liveTrackId] ?? {
        unlinkQueued: false,
        revealDoneMatches: false,
        hasQueuedLink: false,
        hideCandidateRows: false,
      }
    );
  };

  const getState = (
    liveTrackId: string | number,
    source: SourceType,
    sourceId: string | number
  ): TrackMatchingRowState => {
    const key = `${liveTrackId}-${source}-${sourceId}`;
    if (!states.value[key]) {
      states.value[key] = {
        live_track_id: liveTrackId,
        source,
        sourceId,
      };
      attachRowToLiveTrackIndex(states.value[key]);
    }
    return states.value[key];
  };

  const setMatchData = (
    liveTrackId: string | number,
    source: SourceType,
    sourceId: string | number,
    matchData: Record<string, unknown>
  ) => {
    getState(liveTrackId, source, sourceId).matchData = matchData;
  };

  const clearOtherPrimarySave = (liveTrackId: string | number, keep: 'link' | 'isrc' | 'mark') => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);
    if (keep !== 'link' && keep !== 'isrc') {
      clearPendingPrimarySave(liveTrackRow);
    } else if (keep === 'link') {
      if (liveTrackRow.pendingPrimarySave?.kind === 'isrc') {
        clearPendingPrimarySave(liveTrackRow);
      }
    } else if (keep === 'isrc') {
      if (liveTrackRow.pendingPrimarySave?.kind === 'link') {
        clearPendingPrimarySave(liveTrackRow);
      }
    }
    if (keep !== 'mark') {
      liveTrackRow.markQueued = false;
      if (keep === 'link') {
        const statusId = getEffectiveTrackMatchStatusId(
          liveTrackRow.matchData as Record<string, unknown> | undefined
        );
        if (!trackMatchingStatusHidesCandidates(statusId)) {
          liveTrackRow.reveal_done_matches = false;
        }
      } else {
        liveTrackRow.reveal_done_matches = false;
      }
    }
  };

  const queueLinkOnPreviouslyLinkedRow = (liveTrackId: string | number) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);
    const previouslyLinkedId = liveTrackRow.matchData?.raw_ddex_track_id;
    if (previouslyLinkedId == null || previouslyLinkedId === '') {
      return;
    }

    const previouslyLinkedRow = findRawDdexRowStateByLinkedId(
      rowsForLiveTrack(liveTrackId),
      liveTrackId,
      previouslyLinkedId
    );

    if (previouslyLinkedRow) {
      liveTrackRow.pendingPrimarySave = {
        kind: 'link',
        rawDdexSourceId: previouslyLinkedRow.sourceId,
      };
    }
  };

  const toggleQueuedLink = (
    liveTrackId: string | number,
    source: SourceType,
    sourceId: string | number
  ) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);

    if (isLinkQueuedForSource(liveTrackRow, sourceId)) {
      clearPendingPrimarySave(liveTrackRow);
      syncQueueVisibilityForLiveTrack(liveTrackId);
      return;
    }

    clearOtherPrimarySave(liveTrackId, 'link');
    liveTrackRow.pendingPrimarySave = { kind: 'link', rawDdexSourceId: sourceId };
    syncQueueVisibilityForLiveTrack(liveTrackId);
  };

  const toggleQueuedIsrc = (liveTrackId: string | number) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);

    if (isIsrcQueued(liveTrackRow)) {
      clearPendingPrimarySave(liveTrackRow);
      return;
    }

    clearOtherPrimarySave(liveTrackId, 'isrc');
    liveTrackRow.pendingPrimarySave = { kind: 'isrc' };
    syncQueueVisibilityForLiveTrack(liveTrackId);
  };

  /**
   * Queue (or unqueue) an artist-name cleanup on the live track from a Raw DDEX / Spotify row.
   * Clicking the row that's already queued clears it; clicking a different source replaces the
   * target. Independent of {@see toggleQueuedLink} / {@see toggleQueuedIsrc} — it does not touch
   * pendingPrimarySave, so an artist clean can be saved alongside a Link or Apply ISRC.
   */
  const toggleArtistRename = (
    liveTrackId: string | number,
    sourceId: string | number,
    name: string
  ) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);

    if (isArtistRenameQueuedForSourceOnRow(liveTrackRow, sourceId)) {
      clearPendingArtistRename(liveTrackRow);
      syncQueueVisibilityForLiveTrack(liveTrackId);
      return;
    }

    liveTrackRow.pendingArtistRename = { sourceId, name };
    syncQueueVisibilityForLiveTrack(liveTrackId);
  };

  const isArtistRenameQueued = (liveTrackId: string | number): boolean => {
    return isArtistRenameQueuedOnRow(getLiveTrackRow(liveTrackId));
  };

  const isArtistRenameQueuedForSource = (
    liveTrackId: string | number,
    sourceId: string | number
  ): boolean => {
    return isArtistRenameQueuedForSourceOnRow(getLiveTrackRow(liveTrackId), sourceId);
  };

  const toggleUnlink = (liveTrackId: string | number) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);

    if (liveTrackRow.unlinkQueued) {
      liveTrackRow.unlinkQueued = false;
      if (liveTrackRow.pendingPrimarySave?.kind === 'link') {
        clearPendingPrimarySave(liveTrackRow);
      }
      syncQueueVisibilityForLiveTrack(liveTrackId);
      return;
    }

    liveTrackRow.unlinkQueued = true;
    liveTrackRow.markQueued = false;
    queueLinkOnPreviouslyLinkedRow(liveTrackId);
    syncQueueVisibilityForLiveTrack(liveTrackId);
  };

  const toggleQueueAction = (
    liveTrackId: string | number,
    source: SourceType,
    sourceId: string | number,
    action: TrackQueueAction
  ) => {
    switch (action) {
      case 'link':
        toggleQueuedLink(liveTrackId, source, sourceId);
        break;
      case 'isrc':
        toggleQueuedIsrc(liveTrackId);
        break;
      case 'unlink':
        toggleUnlink(liveTrackId);
        break;
    }
  };

  const toggleMarkQueued = (liveTrackId: string | number) => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);
    const persistedStatusId = getEffectiveTrackMatchStatusId(
      liveTrackRow.matchData as Record<string, unknown> | undefined
    );
    const next = computeNextMarkRevealState({
      markQueued: !!liveTrackRow.markQueued,
      revealDoneMatches: liveTrackRow.reveal_done_matches === true,
      persistedStatusId,
      unlinkRawDdexQueued: !!liveTrackRow.unlinkQueued,
    });
    liveTrackRow.markQueued = next.nextMarkQueued;
    liveTrackRow.reveal_done_matches = next.nextRevealDoneMatches;
    if (next.nextMarkQueued) {
      clearOtherPrimarySave(liveTrackId, 'mark');
    } else if (next.shouldClearLinkSelections) {
      clearPendingPrimarySave(liveTrackRow);
    }
    syncQueueVisibilityForLiveTrack(liveTrackId);
  };

  const isMarkQueued = (liveTrackId: string | number): boolean => {
    return !!getLiveTrackRow(liveTrackId).markQueued;
  };

  const isQueueActionSelected = (
    liveTrackId: string | number,
    source: SourceType,
    sourceId: string | number,
    action: TrackQueueAction
  ): boolean => {
    const liveTrackRow = getLiveTrackRow(liveTrackId);
    switch (action) {
      case 'link':
        return isLinkQueuedForSource(liveTrackRow, sourceId);
      case 'isrc':
        return isIsrcQueued(liveTrackRow);
      case 'unlink':
        return !!liveTrackRow.unlinkQueued;
    }
  };

  const getStatesForLiveTrack = (liveTrackId: string | number): TrackMatchingRowState[] => {
    return rowsForLiveTrack(liveTrackId);
  };

  const mergeOptimisticPatch = (liveTrackId: string | number, fragment: Record<string, unknown>) => {
    const fragmentWithStatus =
      fragment.track_match_status_id !== undefined
        ? {
            ...fragment,
            track_match_status: trackMatchStatusRelationFromId(fragment.track_match_status_id),
          }
        : fragment;

    const existing = optimisticPatches.value[liveTrackId] ?? {};
    optimisticPatches.value = {
      ...optimisticPatches.value,
      [liveTrackId]: mergeDeepIncomingWins(existing, fragmentWithStatus),
    };

    const liveState = states.value[liveTrackRowKey(liveTrackId)];
    if (liveState?.matchData != null && fragment.track_match_status_id !== undefined) {
      const relation = trackMatchStatusRelationFromId(fragment.track_match_status_id);
      liveState.matchData = {
        ...liveState.matchData,
        track_match_status_id: fragment.track_match_status_id,
        ...(relation ? { track_match_status: relation } : {}),
      };
    }
  };

  const mergeLiveTrackWithOptimisticPatch = (
    liveTrack: Record<string, unknown>,
    liveTrackId: string | number
  ): Record<string, unknown> => {
    const patch = optimisticPatches.value[liveTrackId] ?? {};
    if (Object.keys(patch).length === 0) {
      return liveTrack;
    }
    return mergeDeepIncomingWins(liveTrack, patch);
  };

  const clearOptimisticPatches = () => {
    optimisticPatches.value = {};
  };

  const clearStates = () => {
    states.value = {};
    queueVisibilityByLiveTrackId.value = {};
    clearRowIndex();
    clearOptimisticPatches();
  };

  const clearActionStates = () => {
    Object.values(states.value).forEach(clearQueuedActionsForRow);
  };

  const clearActionStatesForLiveTracks = (liveTrackIds: ReadonlySet<string | number>) => {
    if (liveTrackIds.size === 0) return;
    Object.values(states.value).forEach((rowState) => {
      if (!liveTrackIds.has(rowState.live_track_id)) return;
      clearQueuedActionsForRow(rowState);
    });
  };

  /**
   * The set of live-track ids with any queued action (primary save, artist rename, unlink, mark).
   * Single source of truth so callers (nav guard, processing guard, payload builder) can't drift on
   * what "has a pending action" means.
   */
  const queuedLiveTrackIds = (): Set<string | number> => {
    return collectQueuedLiveTrackIds(states.value);
  };

  const countQueuedLiveTracks = (): number => {
    return queuedLiveTrackIds().size;
  };

  const generateActionsPayload = (): TrackMatchingAction[] => {
    const liveTrackIds = collectQueuedLiveTrackIds(states.value);
    const actions: TrackMatchingAction[] = [];

    liveTrackIds.forEach((liveTrackId) => {
      const rowStates = getStatesForLiveTrack(liveTrackId);
      const liveTrackRow = getLiveTrackRow(liveTrackId);
      const persistedStatusId = getEffectiveTrackMatchStatusId(
        liveTrackRow.matchData as Record<string, unknown> | undefined
      );

      const action = buildTrackMatchingActionForLiveTrack(liveTrackId, rowStates, persistedStatusId);
      if (action) {
        actions.push(action);
      }
    });

    return actions;
  };

  return {
    states: readonly(states),
    queueVisibilityByLiveTrackId: readonly(queueVisibilityByLiveTrackId),
    getState,
    getLiveTrackRow,
    getQueueVisibilityForLiveTrack,
    syncQueueVisibilityForLiveTrack,
    setMatchData,
    toggleQueueAction,
    toggleMarkQueued,
    toggleArtistRename,
    isArtistRenameQueued,
    isArtistRenameQueuedForSource,
    isQueueActionSelected,
    isMarkQueued,
    getStatesForLiveTrack,
    clearStates,
    clearOptimisticPatches,
    clearActionStates,
    clearActionStatesForLiveTracks,
    countQueuedLiveTracks,
    queuedLiveTrackIds,
    generateActionsPayload,
    mergeOptimisticPatch,
    mergeLiveTrackWithOptimisticPatch,
  };
};
